'use client';

import React, { useMemo } from 'react';

import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import { MagicWallCard, Sorts, WallResponse } from '@shared/api/types/magicWall';
import { formatNumberLocalized } from '@shared/helpers/formatters';

import CategoryFilter from './CategoryFilter';
import { useTransformData } from './hooks';
import InfiniteQueryState from './InfiniteQueryState';
import MagicOrderWallOffersList from './MagicOrderWallOffersList';
import SortFilter from './SortFilter';
import StatInfo from './StatInfo';

type Props = {
  categoryFilter: string;
  setCategoryFilter: (value: string) => void;
  sort: Sorts;
  setSort: (value: Sorts) => void;
  query: UseInfiniteQueryResult<InfiniteData<WallResponse, unknown>, unknown>;
  list: MagicWallCard[];
};

const MagicOrderWallOffersPage = ({
  categoryFilter,
  setCategoryFilter,
  sort,
  setSort,
  query,
  list,
}: Props) => {
  const transformData = useTransformData(query, list);
  const meta = useMemo(() => query.data?.pages.find((page) => page.meta)?.meta, [query.data]);

  const availableReward = meta?.availableSQRReward
    ? formatNumberLocalized(meta?.availableSQRReward)
    : 0;
  const availableKarmaReward = transformData.meta?.availableKarmaReward
    ? formatNumberLocalized(transformData.meta?.availableKarmaReward)
    : 0;

  return (
    <>
      <div className="flex justify-between items-center gap-2 mt-8 mb-6 sm:my-8 flex-wrap xl:flex-nowrap">
        <div className="flex gap-x-6 xl:gap-x-16 gap-y-4 w-full flex-wrap md:flex-nowrap">
          <CategoryFilter categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} />
          <SortFilter sort={sort} setSort={setSort} />
        </div>

        <div className="flex gap-2 w-full justify-end flex-col md:flex-row">
          <StatInfo
            className="w-full 2xl:max-w-[330px]"
            label="Total Available Reward"
            value={`${availableReward} $SQR`}
          />
          <StatInfo
            className="w-full 2xl:max-w-[330px]"
            label="Total Available Karma"
            value={availableKarmaReward}
          />
        </div>
      </div>

      <MagicOrderWallOffersList list={transformData.data} />

      <InfiniteQueryState query={query} />
    </>
  );
};

export default MagicOrderWallOffersPage;
